import React, { useEffect, useState } from 'react';
import { useProfileStore, UploadAchievementImageError } from '../../stores/profileStore';
import { InputGroup } from '../common/InputGroup';
import { useTranslation } from 'react-i18next';
import { useServiceCategorySelector } from '../../hooks/useServiceCategorySelector';
import { ServiceCategory } from '../../types/service';
import { DropdownMultiTypeahead } from '../common/DropdownMultiTypeahead';
import { getUploadErrorTranslationCode } from '../../utils/errors';
import FullBlockingModal from '../common/FullBlockingModal';
import { AchievementImageUpload } from './AchievementImageUpload';
import { ProfileAchievement } from '../../types/profile';
import { toast } from 'react-toastify';
import { createServiceGroupBy } from '../../utils/serviceUtils';

interface AchievementModalProps {
  onClose: () => void;
  achievementToEdit?: ProfileAchievement | null;
  firstAchievementImage?: File | null;
}

const maxFileSize = 5 * 1024 * 1024;

export const AchievementModal: React.FC<AchievementModalProps> = ({
  onClose,
  achievementToEdit,
  firstAchievementImage,
}) => {
  const { t } = useTranslation(['auth', 'common']);
  const { profile, upsertAchievement, deleteAchievement, suggestNewService } = useProfileStore();

  const [projectName, setProjectName] = useState(achievementToEdit?.project_name || '');
  const [clientName, setClientName] = useState(achievementToEdit?.client_name || '');
  const [description, setDescription] = useState(achievementToEdit?.project_description || '');
  const [selectedServices, setSelectedServices] = useState<ServiceCategory[]>(
    achievementToEdit?.services || []
  );
  const [errors, setErrors] = useState<{
    projectName?: string;
    clientName?: string;
    description?: string;
    services?: string;
    image?: string;
    imageSizeError?: string;
  }>({});
  const [file, setFile] = useState<File | undefined>(undefined);
  const [existingImageRemoved, setExistingImageRemoved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    selectedOptions: selectedServiceCategories,
    setSelectedOptions: setSelectedServiceCategories,
    filteredOptions: serviceCategoryOptions,
    setSearchInput: setServiceSearchInput,
  } = useServiceCategorySelector(profile?.services);

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!projectName.trim()) newErrors.projectName = t('fieldRequired', { ns: 'auth' });
    if (!clientName.trim()) newErrors.clientName = t('fieldRequired', { ns: 'auth' });
    if (!description.trim()) newErrors.description = t('fieldRequired', { ns: 'auth' });
    if (!selectedServices.length) newErrors.services = t('fieldRequired', { ns: 'auth' });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const title = achievementToEdit
    ? t('improveProfileStep.achievements.modify', { ns: 'auth' })
    : t('improveProfileStep.achievements.add', { ns: 'auth' });

  const handleSubmit = async () => {
    try {
      if (!profile?.id) {
        throw new Error('Profile ID is required');
      }

      if (!validate()) {
        return;
      }

      setSubmitting(true);

      if (file?.size && file.size > maxFileSize) {
        setErrors(prev => ({
          ...prev,
          imageSizeError: t('improveProfileStep.achievements.imageSize', { ns: 'auth' }),
        }));
        return;
      }

      setErrors({});

      await upsertAchievement({
        id: achievementToEdit?.id,
        profile_id: profile?.id || '',
        project_name: projectName,
        client_name: clientName,
        project_description: description,
        service_category_ids: selectedServices.map(service => service.id),
        imageFile: file,
      });
      onClose();
    } catch (err) {
      console.error('Error creating/updating achievement:', err);
      if (err instanceof UploadAchievementImageError) {
        setErrors({ image: t(getUploadErrorTranslationCode(err.message), { ns: 'common' }) });
      }
      setError(t('error.generic', { ns: 'common' }));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (firstAchievementImage) {
      setFile(firstAchievementImage);
      setExistingImageRemoved(false);
    }
  }, [firstAchievementImage]);

  const handleImageChange = (newFile: File) => {
    setFile(newFile);
    setExistingImageRemoved(false);
  };

  const handleImageRemove = () => {
    setFile(undefined);

    if (achievementToEdit?.project_image_url) {
      setExistingImageRemoved(true);
    }
  };

  const handleDeleteAchievement = async () => {
    if (!achievementToEdit?.id) return;
    const res = await deleteAchievement(achievementToEdit.id);

    if (res === undefined) {
      toast.success(t('improveProfileStep.achievements.deleteSuccess', { ns: 'auth' }));
    } else {
      toast.error(t('improveProfileStep.achievements.deleteError', { ns: 'auth' }));
    }
    onClose();
  };

  const handleCreateNewService = async (query: string) => {
    try {
      const newService = await suggestNewService({
        label: query,
        description: '',
      });
      setSelectedServiceCategories([...selectedServiceCategories, newService]);
      toast.success(t('serviceAddedSuccess', { ns: 'settings', defaultValue: 'Service added!' }));
    } catch (err) {
      console.error('Error creating service:', err);
      toast.error(t('serviceAddError', { ns: 'settings', defaultValue: 'Could not add service.' }));
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[48rem]">
      <h2 className="text-secondary font-ambit text-xl font-black leading-[25px] mb-4">{title}</h2>

      <div className="mb-6">
        <AchievementImageUpload
          file={file}
          achievementId={achievementToEdit?.id}
          imageUrl={!existingImageRemoved ? achievementToEdit?.project_image_url : undefined}
          onChange={handleImageChange}
          onRemove={handleImageRemove}
        />
      </div>

      <InputGroup
        label={t('improveProfileStep.achievements.projectName', { ns: 'auth' })}
        required
        error={!!errors.projectName}
        errorMessage={errors.projectName}
        value={projectName}
        onChange={e => {
          setProjectName(e.target.value);
          if (errors.projectName) setErrors(prev => ({ ...prev, projectName: undefined }));
        }}
      />

      <div className="mt-4 mb-2">
        <InputGroup
          label={t('improveProfileStep.achievements.client', { ns: 'auth' })}
          required
          error={!!errors.clientName}
          errorMessage={errors.clientName}
          value={clientName}
          onChange={e => {
            setClientName(e.target.value);
            if (errors.clientName) setErrors(prev => ({ ...prev, clientName: undefined }));
          }}
        />
      </div>

      <InputGroup
        label={t('servicesOffered', { ns: 'auth' })}
        required
        error={!!errors.services}
        errorMessage={errors.services}
      >
        <DropdownMultiTypeahead
          selected={selectedServices}
          onChange={items => {
            setSelectedServices(items);
            if (errors.services) setErrors(prev => ({ ...prev, services: undefined }));
          }}
          options={serviceCategoryOptions}
          onSearchChange={setServiceSearchInput}
          getOptionLabel={item => item.label}
          getOptionValue={item => item.id}
          placeholder={t('form_fields.servicesHint', { ns: 'common' })}
          className="mt-2"
          error={!!errors.services}
          onCreate={handleCreateNewService}
          groupBy={createServiceGroupBy(t)}
          inputMode="search"
        />
      </InputGroup>

      <div className="mt-4 md:col-span-2">
        <InputGroup
          label={t('improveProfileStep.achievements.projectDetailsLabel', { ns: 'auth' })}
          required
          error={!!errors.description}
          errorMessage={errors.description}
          value={description}
          onChange={e => {
            setDescription(e.target.value);
            if (errors.description) setErrors(prev => ({ ...prev, description: undefined }));
          }}
          as="textarea"
          placeholder={t('improveProfileStep.achievements.projectDetailsPlaceholder', {
            ns: 'auth',
          })}
          maxLength={1500}
        />
        <div className="text-right font-ambit text-[0.625rem] not-italic font-normal leading-[0.9375rem] text-secondary pb-4">
          {1500 - description.length} {t('charactersRemaining', { ns: 'auth' })}
        </div>
      </div>

      {/* Error */}
      {error && <div className="text-red-500 mb-4 text-sm">{error}</div>}

      {/* Actions */}
      <div className="mt-10 flex flex-col gap-2 bg-white pt-2 pb-4">
        {errors.imageSizeError && <p className="text-[#F04438]">{errors.imageSizeError}</p>}
        <button
          onClick={handleSubmit}
          className="w-full bg-primary text-white py-2 px-4 rounded-full font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
          disabled={submitting}
        >
          {submitting ? t('saving', { ns: 'auth' }) : t('improveProfileStep.save', { ns: 'auth' })}
        </button>

        {achievementToEdit?.id && (
          <button
            onClick={handleDeleteAchievement}
            className="w-full mt-8 border border-primary  text-secondary font-bold rounded-full py-2 text-sm hover:bg-primary/10 transition"
          >
            {t('improveProfileStep.achievements.delete', { ns: 'auth' })}
          </button>
        )}

        <button
          onClick={() => {
            setFile(undefined);
            setExistingImageRemoved(false);
            setErrors({ services: undefined });
            setSelectedServices([]);
            setProjectName('');
            setClientName('');
            setDescription('');
            onClose();
          }}
          className="mt-8 w-full text-secondary underline text-base font-bold"
        >
          {t('improveProfileStep.achievements.close', { ns: 'auth' })}
        </button>
      </div>
    </FullBlockingModal>
  );
};
